import { Box } from '@mui/material';
import Loading from 'components/common/Loading';
import { bool, object, shape, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import Http from 'services/Http';
import {
  notifySignatureDeclined,
  notifySignatureSigned,
  notifySignatureViewed,
  refreshPendingSignatureCount,
} from 'components/@home/@signatures/pendingSignatures';
import { getDigitalSignatureCardTitle } from 'utils/getDigitalSignatureCardCopy';

import channelShape from 'shapes/channel';
import employeeShape from 'shapes/employee';
import messageShape from 'shapes/message';
import OmmnioSignScene from 'components/@ommniosign/OmmnioSignScene';
import Modal from '../../Modal';
import connector from './connector';
import Information from './Information';

const SignatureModal = ({
  isSignatureOpen,
  actions,
  message = null,
  employee = null,
  authEmployee = null,
  signUrl = null,
}) => {
  const emp = employee || authEmployee;
  const acknowledge = message?.acknowledges?.find(m => m.user === emp?.user?._id);

  // Parse signUrl to extract requestId and signerToken
  // signUrl format: /ommniosign/sign/:requestId/:signerToken
  const { requestId, signerToken } = useMemo(() => {
    if (!signUrl) return { requestId: null, signerToken: null };
    const match = signUrl.match(/\/ommniosign\/sign\/([^/]+)\/([^/]+)/);
    if (match) {
      return { requestId: match[1], signerToken: match[2] };
    }
    return { requestId: null, signerToken: null };
  }, [signUrl]);

  useEffect(() => {
    if (!isSignatureOpen || !message?.digitalSignature || !message?._id) return;
    const currentUserId = emp?.user?._id || emp?.user;
    const alreadySigned = (message.acknowledges || []).some(
      item => String(item.user?._id || item.user) === String(currentUserId),
    );
    if (alreadySigned) return;
    Http.post(`/v1/signatures/${message._id}/viewed`).catch(() => {});
    notifySignatureViewed(message._id);
  }, [isSignatureOpen, message?._id, message?.digitalSignature, emp?.user, message?.acknowledges]);

  const handleComplete = useCallback(async () => {
    const messageId = message?._id;
    const channelId = message?.channel_id;
    if (messageId) {
      notifySignatureSigned(messageId);
      refreshPendingSignatureCount();
    }
    try {
      if (messageId && channelId) {
        await actions.messages.loadListedMessage(channelId, messageId);
      }
    } finally {
      actions.acknowledgmentReadModal.close();
    }
  }, [actions.acknowledgmentReadModal, actions.messages, message?._id, message?.channel_id]);

  const handleCancel = useCallback(() => {
    const messageId = message?._id;
    const channelId = message?.channel_id;
    if (messageId && channelId) {
      actions.messages.loadListedMessage(channelId, messageId);
    }
    actions.acknowledgmentReadModal.close();
  }, [actions.acknowledgmentReadModal, actions.messages, message?._id, message?.channel_id]);

  const handleDecline = useCallback(() => {
    const messageId = message?._id;
    if (messageId) {
      notifySignatureDeclined(messageId);
      refreshPendingSignatureCount();
    }
    handleCancel();
  }, [handleCancel, message?._id]);

  if (!isSignatureOpen) return null;

  const cardTitle = getDigitalSignatureCardTitle(message);

  return (
    <Modal isOpen={isSignatureOpen} onClose={actions.acknowledgmentReadModal.close}>
      <Box
        sx={{
          position: 'relative',
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="h3"
          sx={{
            color: 'text.primary',
            fontSize: '20px',
            fontWeight: 'bold',
            m: '0 0 15px',
            textAlign: 'center',
            width: '100%',
            wordBreak: 'break-all',
          }}
        >
          {cardTitle}
        </Box>
        <Information confirmedAt={acknowledge?.confirmedAt} isDigitalSignature />
        {requestId && signerToken && isSignatureOpen ? (
          <Box
            sx={{
              maxWidth: 860,
              width: '90vw',
              height: '80vh',
              maxHeight: 'calc(100vh - 250px)',
              minHeight: 0,
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <OmmnioSignScene
              requestId={requestId}
              signerToken={signerToken}
              onComplete={handleComplete}
              onCancel={handleCancel}
              onDecline={handleDecline}
            />
          </Box>
        ) : signUrl ? (
          <Box
            sx={{
              maxWidth: 860,
              width: '90vw',
              height: '80vh',
              maxHeight: 'calc(100vh - 250px)',
              position: 'relative',
            }}
          >
            <Box
              component="iframe"
              title="pdf"
              src={signUrl}
              allow="publickey-credentials-get *; publickey-credentials-create *"
              sx={{
                zIndex: 'initial',
                border: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        ) : (
          <Loading size={40} key="loading" />
        )}
      </Box>
    </Modal>
  );
};

SignatureModal.propTypes = {
  isCompanyAdmin: bool.isRequired,
  isTeamAdmin: bool.isRequired,
  isSignatureOpen: bool.isRequired,
  message: messageShape,
  channel: channelShape,
  actions: shape({
    acknowledgmentReadModal: object.isRequired,
  }).isRequired,
  employee: employeeShape,
  authEmployee: employeeShape,
  signUrl: string,
};

export default connector(SignatureModal);
